import React, { useState, useCallback, useEffect } from 'react';
import type { FC } from 'react';
import { Box } from 'ink';
import { isType } from 'variant';
import { InputState } from '../../input-state.js';
import { InputLabel } from '../InputLabel.js';
import { Input } from './Input.js';
import { Text } from '../Text.js';
import { PointerIcon } from '../../icons/Pointer.js';
import { SuccessIcon } from '../../icons/Success.js';
import { Spinner } from '../Spinner.js';

type ValidationResult = true | string;

export interface TextInputProps {
  label: string;
  isActive?: boolean;
  placeholder?: string;
  hint?: string;
  onSubmit: (value: string) => void;
  validate?: (value: string) => ValidationResult | Promise<ValidationResult>;
  inCreateFlow?: boolean;
}

export const TextInput: FC<TextInputProps> = ({
  label,
  isActive,
  placeholder,
  hint,
  onSubmit,
  validate = () => true,
  inCreateFlow = false,
}) => {
  const [inputState, setInputState] = useState<InputState<string | undefined>>(
    () => InputState.NotSubmitted(placeholder)
  );
  const [isDirty, setIsDirty] = useState(false);
  const [showSpinner, setShowSpinner] = useState(false);
  const [validationError, setValidationError] = useState<string | undefined>();

  const handleSubmit = useCallback(
    async (value: string) => {
      setValidationError(undefined);
      setIsDirty(false);
      const validatingTimer = setTimeout(() => {
        setShowSpinner(true);
      }, 300);

      try {
        const validationResult = await validate(value);

        if (validationResult !== true) {
          setValidationError(validationResult);
        } else {
          setValidationError(undefined);
          setInputState(InputState.Submitted(value));
          onSubmit(value);
        }
      } finally {
        clearTimeout(validatingTimer);
        setShowSpinner(false);
      }
    },
    [onSubmit, validate]
  );

  const afterValidationError = Boolean(validationError && !isDirty);
  const showHint = Boolean(hint) && isType(inputState, InputState.NotSubmitted);

  useEffect(() => {
    if (isActive) {
      setInputState(InputState.NotSubmitted(inputState.value));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive]);

  return (
    <>
      <Box marginBottom={showHint ? 0 : 1} marginLeft={inCreateFlow ? -2 : 0}>
        <Text>
          <InputLabel
            icon={
              <Text skin="question">
                <PointerIcon appearance="triangle" />
              </Text>
            }
          >
            {label}
          </InputLabel>
          {isType(inputState, InputState.NotSubmitted) && (
            <>
              {' '}
              <Text skin={afterValidationError ? 'error' : 'standard'}>
                <Input
                  placeholder={inputState.value}
                  onSubmit={handleSubmit}
                  onChange={() => setIsDirty(true)}
                  showCursor={afterValidationError}
                />
              </Text>
            </>
          )}
        </Text>
      </Box>
      {showHint && (
        <Box marginBottom={1} marginLeft={inCreateFlow ? 0 : 2}>
          <Text skin="secondary">{hint}</Text>
        </Box>
      )}
      {isType(inputState, InputState.Submitted) && (
        <Box marginBottom={1}>
          <Text skin="secondary">
            <SuccessIcon /> {inputState.value}
          </Text>
        </Box>
      )}
      {validationError && (
        <Box columnGap={1}>
          <Text skin="error">
            <PointerIcon appearance="exclamation" />
          </Text>
          <Text skin="error">{validationError}</Text>
        </Box>
      )}
      {showSpinner && (
        <Box columnGap={1}>
          <Text skin="secondary">
            <Spinner /> {'Validating...'}
          </Text>
        </Box>
      )}
    </>
  );
};
